import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import WbSunnyIcon from '@mui/icons-material/WbSunny';

import Message from './Message';

export const DAILY_BRIEF_PROMPT = (
	"Give me my daily brief, concise and actionable. " +
	"Fetch in parallel: my open tasks (mine, sorted by priority), " +
	"my timesheet for today and yesterday, my upcoming leaves in the next 2 weeks, " +
	"the team's timesheet status today, and who is on leave this month. " +
	"If I can approve leaves, also list the pending requests. " +
	"Synthesize into short sections with bullets. End with **Top priority** — the single most important thing to do today. " +
	"Reply in my preferred language."
);

const QUICK_PROMPTS = [
	'List my active projects',
	'How many hours have I logged this week?',
];

const EmptyState = ({ onPick }) => (
	<Box sx={{ textAlign: 'center', pt: 6, pb: 4, color: 'text.secondary' }}>
		<Typography variant="h6" gutterBottom>Hi there 👋</Typography>
		<Typography variant="body2" sx={{ mb: 3 }}>
			I can help you check your projects, timesheet, tasks and team.
		</Typography>
		<Button
			variant="contained"
			size="large"
			startIcon={<WbSunnyIcon />}
			onClick={() => onPick(DAILY_BRIEF_PROMPT)}
			sx={{ mb: 3 }}
		>
			Brief du jour
		</Button>
		<Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
			Or try:
		</Typography>
		<Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
			{QUICK_PROMPTS.map((p) => (
				<Button key={p} size="small" variant="outlined" onClick={() => onPick(p)}>
					{p}
				</Button>
			))}
		</Box>
	</Box>
);

const ChatView = ({ messages, isStreaming, send, stop }) => {
	const [input, setInput] = useState('');
	const bottomRef = useRef(null);

	useEffect(() => {
		if (bottomRef.current) {
			bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
		}
	}, [messages, isStreaming]);

	const handleSubmit = (e) => {
		e.preventDefault();
		if (!input.trim() || isStreaming) return;
		send(input);
		setInput('');
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSubmit(e);
		}
	};

	return (
		<Box sx={{
			flexGrow: 1,
			display: 'flex',
			flexDirection: 'column',
			minHeight: 0,
			minWidth: 0,
			px: 2,
		}}>
			<Box sx={{
				flexGrow: 1,
				overflowY: 'auto',
				minHeight: 0,
				py: 2,
			}}>
				{messages.length === 0 ? (
					<EmptyState onPick={send} />
				) : (
					messages.map((m) => <Message key={m.id} message={m} />)
				)}
				<div ref={bottomRef} />
			</Box>

			<Paper
				component="form"
				onSubmit={handleSubmit}
				elevation={0}
				sx={{
					display: 'flex',
					alignItems: 'flex-end',
					gap: 1,
					p: 1,
					mt: 1,
					mb: 1,
					width: '100%',
					flexShrink: 0,
					border: '1px solid',
					borderColor: 'divider',
					borderRadius: 2,
					'&:focus-within': { borderColor: 'primary.main' },
				}}
			>
				<TextField
					placeholder="Ask anything…"
					value={input}
					onChange={(e) => setInput(e.target.value)}
					onKeyDown={handleKeyDown}
					multiline
					maxRows={6}
					autoFocus
					variant="standard"
					InputProps={{ disableUnderline: true }}
					sx={{ flex: 1, px: 1, py: 0.5 }}
				/>
				{isStreaming ? (
					<Tooltip title="Stop generating">
						<IconButton color="error" onClick={stop}>
							<StopIcon />
						</IconButton>
					</Tooltip>
				) : (
					<Tooltip title="Send (Enter)">
						<span>
							<IconButton color="primary" type="submit" disabled={!input.trim()}>
								<SendIcon />
							</IconButton>
						</span>
					</Tooltip>
				)}
			</Paper>
		</Box>
	);
};

export default ChatView;
